export interface SeoConfig {
  title: string
  description: string
  path: string
  image?: string
  type?: 'website' | 'article' | 'profile'
  noIndex?: boolean
}

const SITE_NAME = 'Hot Sales Girls Academy'
const SITE_URL = 'https://www.hotsalesgirls.com'
const DEFAULT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/w2xwVneUjqSfENHkrW3TJ4/premium-closerin-werden-hot-sales-girls-academy.webp'

function upsertMeta(
  attribute: 'name' | 'property',
  key: string,
  content: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  )
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attribute, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Writes all invisible SEO metadata (title, description, Open Graph, Twitter
 * cards, canonical, viewport, robots) into <head>. Purely technical — it never
 * renders anything and never touches the visual layout.
 */
export function applySeo(config: SeoConfig): void {
  const { title, description, path, image = DEFAULT_IMAGE, type = 'website', noIndex = false } =
    config
  const url = `${SITE_URL}${path}`

  document.title = title

  upsertMeta('name', 'viewport', 'width=device-width, initial-scale=1, viewport-fit=cover')
  upsertMeta('name', 'description', description)
  upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow')
  upsertMeta('name', 'author', SITE_NAME)
  upsertMeta('name', 'theme-color', '#f498c2')

  upsertMeta('property', 'og:site_name', SITE_NAME)
  upsertMeta('property', 'og:locale', 'de_DE')
  upsertMeta('property', 'og:type', type)
  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', description)
  upsertMeta('property', 'og:url', url)
  upsertMeta('property', 'og:image', image)
  upsertMeta('property', 'og:image:alt', title)

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', description)
  upsertMeta('name', 'twitter:image', image)

  upsertLink('canonical', url)
}

/**
 * Injects a JSON-LD structured data block into <head> under a stable id so it
 * can be replaced on navigation. Invisible to users.
 */
export function applyStructuredData(id: string, data: unknown): void {
  let el = document.head.querySelector<HTMLScriptElement>(`script[data-seo="${id}"]`)
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.setAttribute('data-seo', id)
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}
